<template>
    <PageWrapper :loading="isLoading" non-destructive-on-loading>
        <CreateEditRiskProfile
            v-if="initialLoadComplete"
            :custom-properties-status="sortedCustomPropertiesStatus"
            :loading-custom-properties="loadingCustomProperties"
            :risk-profile="localRiskProfile"
            :requirement-types="requirementTypes"
            :requirement-models="getRequirementModelsWithCountryLabel"
            :criteria-templates="templatesStatus.list"
            :criteria="localCriteria"
            :original-criteria="criteriaCopy"
            :expanded-requirement-types="expandedRequirementTypes"
            :loading-requirement-types-and-countries="loadingCoverageTypesAndCountries"
            :collateral-enabled="isCollateralEnabled"
            @load-custom-properties="loadConfig"
            @update-expanded-requirement-types="updateExpandedRequirementTypes"
            @profile-input="onRiskProfileInput"
            @criteria-input="onCriteriaInput"
            @save="save"
            @revert-risk-profile-changes="revertChanges"
        />
        <RiskProfileSetupModal
            v-if="showRiskProfileSetupModal && !isLoading"
            :country-code-list="countryCodeList"
            :categorized-enum-labels="categorizedEnumLabels"
            :collateral-enabled="isCollateralEnabled"
            @confirm="confirmCountryCode"
            @abort="abortCountryCodeConfirmation"
        />
        <ConfirmNavigationOnDirtyState v-if="!isLoading" :dirty="modified" ignore-query />
    </PageWrapper>
</template>

<style lang="scss">
.RiskProfileView {
    .Page .MainLayout {
        background-color: var(--eidBgGrayTones-lightest);
    }
}
</style>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import cloneDeep from 'lodash/cloneDeep';
    import isEqual from 'lodash/isEqual';
    import noop from 'lodash/noop';

    import { Tab, Tabs } from '@evidentid/dashboard-commons/components/Tabs';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { Criterion, CriterionInput } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import {
        RiskProfile,
        RiskProfileInput,
        RiskProfileType,
    } from '@evidentid/tprm-portal-lib/models/decisioning/RiskProfile.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import {
        TprmRequirementModel,
    } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmNavigationOnDirtyState from '@/components/confirm-navigation-on-dirty-state/ConfirmNavigationOnDirtyState.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { useDashboardConfigurationStore } from '@/modules/dashboard-configuration/store';
    import CreateEditRiskProfile
        from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/CreateEditRiskProfile.vue';
    import RiskProfiles
        from '@/modules/decisioning-criteria/components/RiskProfiles/RiskProfiles.vue';
    import RiskProfileSetupModal
        from '@/modules/decisioning-criteria/components/RiskProfileSetupModal/RiskProfileSetupModal.vue';
    import { ConfirmEventPayload } from '@/modules/decisioning-criteria/models/ConfirmEventPayload.model';
    import {
        CriteriaStatus,
        CriterionTemplatesStatus,
        RequirementModelsStatus,
        RequirementTypesPerCountryStatus,
        RiskProfileCopyDraftStatus,
        RiskProfileStatus,
        createEmptyRiskProfileCopyDraftStatus,
        useDecisioningCriteriaStore,
    } from '@/modules/decisioning-criteria/store';
    import {
        RISK_PROFILE_AUTO_ADD_UMBRELLA_CREATE_PROFILE_ID,
        assignCreateGroupAutoUmbrellaStatusId,
        confirmDisablingRiskProfileAutoAddUmbrella,
    } from '@/modules/decisioning-criteria/utils/autoAddUmbrella';
    import { convertCriteriaChangesToPatches } from '@/modules/decisioning-criteria/utils/riskProfileCriteria';
    import { syncCriteriaWithTemplates } from '@/modules/decisioning-criteria/utils/syncCriteriaWithTemplates';
    import { usePageLayoutStore } from '@/modules/page-layout/store';
    import {
        getRequirementTypeLabelByCountry,
    } from '@/utils/get-requirement-type-label-by-country/getRequirementTypeLabelByCountry';
    import { getSortedCustomProperties } from '@/utils/get-sorted-custom-properties/getSortedCustomProperties';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    @Component({
        components: {
            ConfirmNavigationOnDirtyState,
            CreateEditRiskProfile,
            PageWrapper,
            RiskProfileSetupModal,
            RiskProfiles,
            Tab,
            Tabs,
        },
    })
    export default class RiskProfileView extends Vue {
        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private lastRpName!: string;
        private localRiskProfile: RiskProfileInput | RiskProfile =
            this.getDefaultEmptyRiskProfile();

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
            this.pageLayoutStore.setNonDestructiveOnLoading(true);
        }

        @Watch('isLoading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.isLoading);
        }
        private isRiskProfileNameInputInModal: boolean = false;
        private syncedCriteria: (Criterion | CriterionInput)[] = [];
        private localCriteria: (Criterion | CriterionInput)[] = [];
        private forcedNavigate: boolean = false;
        private riskProfileCopy = this.getDefaultEmptyRiskProfile();
        private criteriaCopy: (Criterion | CriterionInput)[] = [];

        private get rpName(): string {
            return this.$rp.current;
        }

        private get showRiskProfileSetupModal(): boolean {
            if (this.isCreateRiskProfileRoute && this.copyRiskProfileDraft.riskProfile) {
                return false;
            }
            const { countryCode, displayName } = this.currentRiskProfileStatus.data || {};
            const hasCountryCode = countryCode || this.localRiskProfile.countryCode;
            const hasRiskProfileName = displayName || this.isRiskProfileNameInputInModal;
            return !hasCountryCode || !hasRiskProfileName;
        }

        private get riskProfileId(): string {
            return this.$route.params.id as string;
        }

        private get isCreateRiskProfileRoute(): boolean {
            return !this.riskProfileId;
        }

        private get expandedRequirementTypes(): TprmRequirementType[] {
            const queryParam = this.$route.query?.expanded;
            if (!queryParam) {
                return [];
            }

            // Handle both single string and array of strings from query
            const expandedFromQuery = Array.isArray(queryParam) ? queryParam : [ queryParam ];
            return expandedFromQuery.filter((type): type is string => typeof type === 'string') as TprmRequirementType[];
        }

        private get riskProfile(): RiskProfileInput | RiskProfile {
            if (this.isCreateRiskProfileRoute && this.copyRiskProfileDraft.riskProfile) {
                return this.copyRiskProfileDraft.riskProfile;
            }
            return this.currentRiskProfileStatus.data || this.getDefaultEmptyRiskProfile();
        }

        private get criteria(): Criterion[] {
            if (this.isCreateRiskProfileRoute && this.copyRiskProfileDraft.criteria.length > 0) {
                return this.copyRiskProfileDraft.criteria as Criterion[];
            }
            return this.riskProfileId ? this.criteriaStatus.criteria[this.riskProfileId] || [] : [];
        }

        private get copyRiskProfileDraft(): RiskProfileCopyDraftStatus {
            return useDecisioningCriteriaStore().copyRiskProfileDraft[this.rpName] ||
                createEmptyRiskProfileCopyDraftStatus();
        }

        private get currentRiskProfileStatus(): RiskProfileStatus {
            return useDecisioningCriteriaStore().currentRiskProfile[this.rpName] || {
                status: OperationStatus.uninitialized,
                data: null,
            };
        }

        private get criteriaStatus(): CriteriaStatus {
            return useDecisioningCriteriaStore().criteria[this.rpName] || {
                status: OperationStatus.uninitialized,
                criteria: {},
            };
        }

        private get templatesStatus(): CriterionTemplatesStatus {
            return useDecisioningCriteriaStore().criterionTemplates[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get requirementTypesPerCountryStatus(): RequirementTypesPerCountryStatus {
            return useDecisioningCriteriaStore().requirementTypesPerCountry[this.rpName] || {
                status: OperationStatus.uninitialized,
                data: {},
            };
        }

        private get creatingRiskProfileStatus(): OperationStatus {
            return useDecisioningCriteriaStore().creatingRiskProfileStatus[this.rpName] ||
                OperationStatus.uninitialized;
        }

        private get creatingCriteriaStatus(): OperationStatus {
            return useDecisioningCriteriaStore().creatingCriteriaStatus[this.rpName] ||
                OperationStatus.uninitialized;
        }

        private get patchingCriteriaStatus(): OperationStatus {
            return useDecisioningCriteriaStore().patchingCriteriaStatus[this.rpName] ||
                OperationStatus.uninitialized;
        }

        private get updatingRiskProfileStatus(): OperationStatus {
            return useDecisioningCriteriaStore().updatingRiskProfileStatus[this.rpName] ||
                OperationStatus.uninitialized;
        }

        private get requirementModelsStatus(): RequirementModelsStatus {
            return useDecisioningCriteriaStore().requirementModelsStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get loadingCoverageTypesAndCountries(): boolean {
            return this.requirementTypesPerCountryStatus.status === OperationStatus.loading;
        }

        private get loadingCurrentRiskProfile(): boolean {
            return this.currentRiskProfileStatus.status === OperationStatus.loading;
        }

        private get loadingCriteria(): boolean {
            return this.criteriaStatus.status === OperationStatus.loading;
        }

        private get loadingTemplates(): boolean {
            return this.templatesStatus.status === OperationStatus.loading;
        }

        private get savingRiskProfile(): boolean {
            return this.creatingRiskProfileStatus === OperationStatus.loading ||
                this.updatingRiskProfileStatus === OperationStatus.loading;
        }

        private get savingCriteria(): boolean {
            return this.creatingCriteriaStatus === OperationStatus.loading ||
                this.patchingCriteriaStatus === OperationStatus.loading;
        }

        private get isLoadingRequirementModels(): boolean {
            return this.requirementModelsStatus.status === OperationStatus.loading;
        }

        /**
         * Whether the editor has ever had a complete data set, latched on once.
         *
         * The editor is withheld until the first full load rather than mounted against a partial
         * one: a criterion whose custom-property picker is built before the properties arrive is
         * built from an empty list, and a picker with nothing to offer collapses, so a criterion
         * already bound to a property is judged invalid and the row keeps that verdict.
         *
         * Latched rather than read from `isInitialising` directly, because that getter goes true
         * again on every later reload -- `save()` re-runs `loadCurrentRiskProfile` and
         * `loadCriteria`, and the child can re-trigger `loadConfig` itself. Gating the `v-if` on
         * it would therefore unmount `CreateEditRiskProfile` after every save and discard
         * everything it holds locally: `invalidReferences`, `accessedMap`,
         * `startShowingValidationErrors` and the modal flags. `PageWrapper` covers those reloads
         * already -- it hides its slot with `v-show` rather than dropping it -- so there is
         * nothing for this to do a second time.
         */
        private initialLoadComplete: boolean = false;

        @Watch('isInitialising', { immediate: true })
        private latchInitialLoadComplete(initialising: boolean): void {
            if (!initialising) {
                this.initialLoadComplete = true;
            }
        }

        /** Every request the editor derives state from, custom properties included. */
        private get isInitialising(): boolean {
            return this.loadingCoverageTypesAndCountries ||
                this.loadingTemplates ||
                this.loadingCurrentRiskProfile ||
                this.loadingCriteria ||
                this.loadingCustomProperties ||
                this.isLoadingRequirementModels;
        }

        private get isLoading(): boolean {
            return this.isInitialising ||
                this.savingRiskProfile ||
                this.savingCriteria;
        }

        private get rpConfigState(): {
            status: OperationStatus;
            customProperties: CustomProperty[];
            customPropertiesOrder: string[];
            entitiesTableCustomProperties: string[];
        } {
            return useDashboardStore().rpConfig[this.rpName] ?? {
                status: OperationStatus.uninitialized,
                customProperties: [],
                customPropertiesOrder: [],
                entitiesTableCustomProperties: [],
            };
        }

        private get sortedCustomPropertiesStatus(): { status: OperationStatus, list: CustomProperty[] } {
            return {
                status: this.rpConfigState.status,
                list: getSortedCustomProperties(
                    this.rpConfigState.customProperties,
                    this.rpConfigState.customPropertiesOrder,
                ),
            };
        }

        private get loadingCustomProperties(): boolean {
            return this.rpConfigState.status === OperationStatus.loading;
        }

        private get countryCode(): string {
            return this.currentRiskProfileStatus.data?.countryCode
                || (this.localRiskProfile as RiskProfileInput).countryCode
                || 'US';
        }

        private get countryCodeList(): string[] {
            return Object.keys(this.requirementTypesPerCountryStatus.data);
        }

        private get categorizedEnumLabels(): CategorizedEnumLabels {
            return useDashboardStore().categorizedEnumLabels;
        }

        private get requirementTypes(): TprmRequirementType[] {
            return this.requirementTypesPerCountryStatus.data[this.countryCode]?.coverageTypes || [];
        }

        private get modified(): boolean {
            const baselineRiskProfile = this.isCreateRiskProfileRoute
                ? this.getDefaultEmptyRiskProfile()
                : this.riskProfile;
            const baselineCriteria = this.isCreateRiskProfileRoute
                ? []
                : this.syncedCriteria;
            return this.forcedNavigate
                ? false
                : !isEqual(baselineRiskProfile, this.localRiskProfile) ||
                    !isEqual(baselineCriteria, this.localCriteria);
        }

        private get getRequirementModelsWithCountryLabel(): TprmRequirementModel[] {
            const countryCode = this.localRiskProfile.countryCode;
            return this.requirementModelsStatus.list.map((model) => {
                const label = getRequirementTypeLabelByCountry(
                    model.coverageType, countryCode, this.categorizedEnumLabels,
                );
                return { ...model, label };
            });
        }

        @Watch('$route')
        private onRouteChange(route: any, prevRoute: any): void {
            const isSwitchRp = route.params.id === prevRoute.params.id && route.params.rpId !== prevRoute.params.rpId;
            if (isSwitchRp) {
                this.clearPageData(prevRoute.params.rpId);
                this.$router.push({
                    name: 'decisioning',
                    params: {
                        rpId: route.params.rpId,
                    },
                }).catch((error) => {
                    if (error.message.includes('Navigation cancelled')) {
                        return;
                    }
                    throw error;
                });
            } else if (!isEqual(route.params, prevRoute.params)) {
                this.loadPageData();
            }
        }

        @Watch('$rp.current', { immediate: true })
        private handleRpChange(rpName: string, prevRpName: string) {
            // Save information about the last resource, which should be cleared during destroy
            this.lastRpName = rpName;
            if (prevRpName) {
                this.clearPageData(prevRpName);
                this.$router.push({
                    name: 'decisioning',
                    params: {
                        rpId: this.$route.params.rpId,
                    },
                });
            } else {
                this.loadPageData();
            }
        }

        @Watch('riskProfile', { immediate: true })
        private onRiskProfileChange(current: RiskProfile, prev: RiskProfile) {
            if (!isEqual(current, prev)) {
                this.localRiskProfile = { ...this.riskProfile };
                this.riskProfileCopy = cloneDeep(this.localRiskProfile);
            }
        }

        @Watch('criteria', { immediate: true })
        @Watch('templatesStatus.list', { immediate: true })
        private onCriteriaChange(current: Criterion[], prev: Criterion[]) {
            if (!isEqual(current, prev)) {
                // syncedCriteria to not trigger unsaved modal when we sync(due to data issue) as it's not user action
                this.syncedCriteria = syncCriteriaWithTemplates(this.criteria, this.templatesStatus.list);
                this.localCriteria = [ ...this.syncedCriteria ];
                this.criteriaCopy = cloneDeep(this.localCriteria);
            }
        }

        private async loadPageData(): Promise<void> {
            await this.loadCurrentRiskProfile();
            this.loadCriteria();
            this.loadTemplates();
            this.loadCoverageTypesAndCountries();
            this.loadConfig();
            // FIXME: temporary flag, remove once all rp migrated to decisioning engine
            this.loadRequirementModels();
        }

        private async loadCurrentRiskProfile(): Promise<void> {
            if (!this.loadingCurrentRiskProfile && this.riskProfileId != null) {
                await useDecisioningCriteriaStore().loadRiskProfile({
                    rpName: this.rpName,
                    id: this.riskProfileId,
                });
            }
        }

        private loadCriteria(): void {
            if (!this.loadingCriteria && this.riskProfileId != null) {
                useDecisioningCriteriaStore().loadCriteria({
                    rpName: this.rpName,
                    riskProfileId: this.riskProfileId,
                });
            }
        }

        private loadTemplates(): void {
            const countryCode = this.localRiskProfile?.countryCode;
            if (!this.loadingTemplates && countryCode) {
                useDecisioningCriteriaStore().loadCriterionTemplates({
                    rpName: this.rpName,
                    countryCode,
                });
            }
        }

        private loadCoverageTypesAndCountries(): void {
            if (!this.loadingCoverageTypesAndCountries) {
                useDecisioningCriteriaStore().loadRequirementTypesPerCountry({ rpName: this.rpName });
            }
        }

        private loadConfig() {
            if (this.rpConfigState.status !== OperationStatus.loading) {
                useDashboardStore().loadRpConfig({ rpName: this.rpName });
            }
        }

        private async save(data: {
            riskProfile: RiskProfile | RiskProfileInput;
            criteria: CriterionInput[] | Criterion[];
        }): Promise<void> {
            // TODO: consider not to emit profile and criteria since it's updated on input. emit active or discovery
            const { riskProfile, criteria } = data;
            if ('id' in riskProfile && riskProfile.id) {
                await this.patchCriteria(
                    (riskProfile as RiskProfile).id,
                    criteria,
                );
                await this.updateRiskProfile(riskProfile as RiskProfile);
                await this.loadCurrentRiskProfile();
                this.loadCriteria();
                confirmDisablingRiskProfileAutoAddUmbrella(riskProfile.id);
            } else {
                await this.createRiskProfile(riskProfile as RiskProfileInput);
                await this.createCriteria(
                    this.currentRiskProfileStatus.data?.id || null,
                    criteria as CriterionInput[],
                );
                if (this.currentRiskProfileStatus.data?.id) {
                    // for auto add umbrella coverage type feature: confirm disabling auto add, and record to session
                    confirmDisablingRiskProfileAutoAddUmbrella(RISK_PROFILE_AUTO_ADD_UMBRELLA_CREATE_PROFILE_ID);
                    // carry over autoUmbrellaStatus in session storage since profile created
                    assignCreateGroupAutoUmbrellaStatusId(this.currentRiskProfileStatus.data.id);
                    // since create profile view does not associate with an id, the data to create profile is saved to a
                    // new profile thus at this navigation to the new profile will always have dirty state, thus reset
                    // dirty state before navigated to newly created profile
                    this.forcedNavigate = true;
                    useDecisioningCriteriaStore().clearRiskProfileCopyDraft({ rpName: this.rpName });
                    this.$router.push({
                        name: 'decisioningRiskProfile',
                        params: {
                            rpId: this.$route.params.rpId,
                            id: this.currentRiskProfileStatus.data.id,
                        },
                    });
                }
            }
        }

        private async createRiskProfile(riskProfile: RiskProfileInput): Promise<void> {
            if (this.creatingRiskProfileStatus !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().createRiskProfile({
                    rpName: this.rpName,
                    riskProfile,
                });
            }
        }

        private async updateRiskProfile(riskProfile: RiskProfile): Promise<void> {
            if (this.updatingRiskProfileStatus !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().updateRiskProfile({
                    rpName: this.rpName,
                    riskProfile,
                });
            }
        }

        private async createCriteria(
            riskProfileId: string | null,
            criteria: CriterionInput[],
        ): Promise<void> {
            if (riskProfileId && this.creatingCriteriaStatus !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().createCriteria({
                    rpName: this.rpName,
                    riskProfileId,
                    criteria,
                });
            }
        }

        private async patchCriteria(
            riskProfileId: string,
            criteria: CriterionInput[] | Criterion[],
        ): Promise<void> {
            const patches = convertCriteriaChangesToPatches(this.criteria, criteria);
            if (this.patchingCriteriaStatus !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().patchCriteria({
                    rpName: this.rpName,
                    riskProfileId,
                    patches,
                });
            }
        }

        private confirmCountryCode({ countryCode, displayName, riskProfileType }: ConfirmEventPayload): void {
            this.isRiskProfileNameInputInModal = true;
            this.localRiskProfile = { ...this.localRiskProfile, countryCode, displayName, ccgType: riskProfileType };
            this.loadTemplates();
        }

        private abortCountryCodeConfirmation(): void {
            this.$router.push({
                name: 'decisioning',
                params: {
                    rpId: this.$route.params.rpId,
                },
            });
        }

        private updateExpandedRequirementTypes(expandedTypes: TprmRequirementType[]): void {
            const query = { ...this.$route.query };

            if (expandedTypes.length === 0) {
                // Remove expanded query param if no types are expanded
                delete query.expanded;
            } else {
                // Update expanded query param with the new list
                query.expanded = expandedTypes;
            }

            this.$router.push({
                ...this.$route,
                query,
            } as any).catch(noop);
        }

        private onRiskProfileInput(riskProfile: RiskProfile): void {
            this.localRiskProfile = { ...riskProfile };
        }

        private onCriteriaInput(criteria: (Criterion | CriterionInput)[]) {
            this.localCriteria = [ ...criteria ];
        }

        private clearPageData(rp?: string) {
            const rpName = rp || this.lastRpName;
            useDecisioningCriteriaStore().clearCurrentRiskProfiles({ rpName });
            useDashboardStore().clearClientRequirementTypeList({ rpName });
            useDecisioningCriteriaStore().clearCreatingRiskProfileStatus({ rpName });
            useDecisioningCriteriaStore().clearUpdatingRiskProfileStatus({ rpName });
            useDecisioningCriteriaStore().clearCreatingCriteriaStatus({ rpName });
            useDecisioningCriteriaStore().clearPatchingCriteriaStatus({ rpName });
            useDecisioningCriteriaStore().clearRpCriteria({ rpName });
            useDecisioningCriteriaStore().clearCriterionMessage({ rpName });
            useDecisioningCriteriaStore().clearRiskProfileCopyDraft({ rpName });
            useDashboardConfigurationStore().clearUpdateDisplayStatus({ rpName });
            useDecisioningCriteriaStore().clearRequirementModelsStatus({ rpName: this.lastRpName });
        }

        private get isCollateralEnabled(): boolean {
            return this.$rp.currentDetails?.collateralsEnabled || false;
        }

        private loadRequirementModels() {
            if (!this.isLoadingRequirementModels) {
                useDecisioningCriteriaStore().loadRequirementModels({ rpName: this.rpName || '' });
            }
        }

        private revertChanges(): void {
            this.localRiskProfile = this.riskProfileCopy;
            this.localCriteria = this.criteriaCopy;
        }

        private getDefaultEmptyRiskProfile(): RiskProfileInput {
            return {
                displayName: '',
                active: false,
                ccgType: RiskProfileType.default,
                numberOfCoverageTypes: null,
                numberOfInsuredsAssigned: null,
                assignmentRule: null,
                countryCode: null,
                assignedEntityInfo: null,
            };
        }

        private unmounted() {
            this.clearPageData();
        }
    }
</script>
